import {
  richTextResolver,
  StoryblokRichTextDocumentNode,
  StoryblokRichTextNode,
  StoryblokRichTextResolvers,
  BlockTypes,
} from "@storyblok/richtext";
import createDOMPurify from "dompurify";
import { renderHtmlNodes } from "@utils/renderHtmlNodes";
import { renderImage } from "@utils/renderImage";
import { renderRichTextComponent } from "@utils/renderRichTextComponent";
import { splitStyleDeclarations } from "@utils/splitStyleDeclarations";

const richTextPurify = createDOMPurify(globalThis);

// Strip pasted inline styles (e.g. color, font-family) that editors may inadvertently
// copy from Google Docs or other sources into Storyblok. Left unsanitized, those styles
// can break the design (e.g. black text on a dark background).
richTextPurify.addHook("uponSanitizeAttribute", (_node, data) => {
  if (data.attrName === "style") {
    const kept = splitStyleDeclarations(data.attrValue)
      .filter((decl) => {
        const prop = decl.split(":")[0].trim();
        return prop.startsWith("--");
      })
      .map((decl) => {
        const colonIndex = decl.indexOf(":");
        const prop = decl.slice(0, colonIndex).trim();
        const val = decl.slice(colonIndex + 1).trim();
        return `${prop}: ${val}`;
      });

    if (kept.length === 0) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = kept.join("; ");
  }
});

interface RichTextProps {
  document: StoryblokRichTextDocumentNode;
  className?: string;
}

export const RichText = ({ document, className = "" }: RichTextProps) => {
  if (!document?.content || document.content.length === 0) return null;

  const html = richTextResolver({
    resolvers: {
      [BlockTypes.TABLE]: (node: StoryblokRichTextNode) =>
        `<table class="flex w-auto overflow-x-scroll custom-scrollbar -mx-4">${renderHtmlNodes(node.children)}</table>`,
      [BlockTypes.TABLE_CELL]: (node: StoryblokRichTextNode) =>
        `<td class="px-4 py-2 text-left">${renderHtmlNodes(node.children)}</td>`,
      [BlockTypes.TABLE_HEADER]: (node: StoryblokRichTextNode) =>
        `<th class="px-4 py-2 font-semibold text-left">${renderHtmlNodes(node.children)}</th>`,
      [BlockTypes.IMAGE]: renderImage,
      [BlockTypes.COMPONENT]: renderRichTextComponent,
    } satisfies StoryblokRichTextResolvers<string>,
  }).render(document as StoryblokRichTextNode);

  // Sanitize the generated HTML. "style" must remain in ADD_ATTR so DOMPurify passes the
  // attribute through to the hook above; the hook then decides what to keep.
  const safeHtml = richTextPurify.sanitize(html, {
    ADD_ATTR: [
      "target",
      "rel",
      "style",
      "data-align",
      "data-component",
      "data-has-width",
      "data-has-height",
      "data-wrap-text",
    ],
  });

  return (
    <div
      className={`richText ${className}`}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};
